import cloneDeep from 'lodash/cloneDeep'
import axios from '~/data/http/axios'
import DragDropFunctions from '~/utils/TemplateEditor/dragDropFunctions'

function mapComponentOptions(rows) {
    return (rows || []).map((row) => DragDropFunctions.replaceGlobalOptions(row.options || row))
}

async function safeComponentList(url) {
    try {
        const res = await axios.get(url)
        return res.data?.TemplateComponents || res.data || []
    } catch {
        return []
    }
}

async function loadCatalog(kind) {
    if (kind === 'landing') {
        const [bodyRes, componentsRes, layouts, blocks, userBlocks] = await Promise.all([
            axios.get('/theme/100'),
            axios.get('/landingPage/components'),
            safeComponentList('/templateComponent/11'),
            safeComponentList('/templateComponent/12'),
            safeComponentList('/templateComponent/101')
        ])
        return {
            bodyJson: cloneDeep(bodyRes.data.Theme.templateJson),
            components: mapComponentOptions(componentsRes.data),
            layouts: mapComponentOptions(layouts),
            blocks: mapComponentOptions([].concat(blocks, userBlocks))
        }
    }

    const [bodyRes, elements, layouts, blocks, market, userBlocks] = await Promise.all([
        axios.get('/theme/98'),
        safeComponentList('/templateComponent/0'),
        safeComponentList('/templateComponent/1'),
        safeComponentList('/templateComponent/2'),
        safeComponentList('/templateComponent/99'),
        safeComponentList('/templateComponent/100')
    ])
    return {
        bodyJson: cloneDeep(bodyRes.data.Theme.templateJson),
        components: mapComponentOptions([].concat(elements, market)),
        layouts: mapComponentOptions(layouts),
        blocks: mapComponentOptions([].concat(blocks, userBlocks))
    }
}

/**
 * Compiles drag-drop JSON to HTML the same way the editor does on save,
 * then persists it so preview/send-test use the filled content instead of
 * the cloned inspiration HTML.
 */
export async function persistAiGeneratedMarkup({ kind, templateId, landingPageId, templateJson }) {
    let simpleJson = templateJson
    let template
    if (!simpleJson && kind === 'email' && templateId) {
        const res = await axios.get(`/template/${templateId}`)
        template = res.data?.template
        simpleJson = template?.templateJson
    }
    if (!simpleJson && kind === 'landing' && landingPageId) {
        const res = await axios.get(`/landingpage/${landingPageId}`)
        simpleJson = res.data?.formJson
    }
    if (!simpleJson || !Array.isArray(simpleJson.structure) || !simpleJson.structure.length) {
        throw new Error('Generated template is missing layout data')
    }

    // Same pass the drag-drop editor runs in loadTemplate before it compiles HTML.
    simpleJson = DragDropFunctions.replaceGlobalOptions(simpleJson)
    if (!simpleJson.version) {
        simpleJson = DragDropFunctions.convertToNewDataFormat(simpleJson, kind === 'landing')
    }

    const previousType = DragDropFunctions.templateType
    DragDropFunctions.templateType = kind === 'landing' ? 'landingPage' : ''

    try {
        const catalog = await loadCatalog(kind)
        let full = DragDropFunctions.convertToFullJson(
            simpleJson,
            catalog.bodyJson,
            catalog.layouts,
            catalog.blocks,
            catalog.components
        )
        if (kind === 'landing' && typeof DragDropFunctions.checkAndInsertLandingPageFreeBanner === 'function') {
            full = await DragDropFunctions.checkAndInsertLandingPageFreeBanner(full)
        } else if (typeof DragDropFunctions.checkAndInsertFreeBanner === 'function') {
            full = await DragDropFunctions.checkAndInsertFreeBanner(full)
        }

        const html = DragDropFunctions.generateHtmlFromJson(
            full,
            false,
            full.globalVariables,
            undefined,
            undefined,
            false,
            kind === 'landing'
        )
        if (!html || String(html).length < 80) {
            throw new Error('Could not build a preview for this template')
        }

        const savedJson = DragDropFunctions.convertToSimpleJson(full)
        if (kind === 'landing') {
            const putRes = await axios.put(`/landingpage/${landingPageId}`, {
                formJson: savedJson,
                formHTML: html
            })
            return { html, url: putRes.data?.url }
        }

        await axios.put(`/template/${templateId}`, {
            templateJson: savedJson,
            templateHTML: html
        })
        return { html, template }
    } finally {
        DragDropFunctions.templateType = previousType
    }
}
